import React, { useEffect } from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { Capacitor } from '@capacitor/core'
import { SplashScreen as NativeSplashScreen } from '@capacitor/splash-screen'
import { StatusBar, Style } from '@capacitor/status-bar'
import App from './App'
import { AuthProvider } from './lib/auth'
import { ThemeProvider } from './lib/theme'
import './i18n' // Initialize i18n
import './index.css'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000, // 1 minute
      retry: 1,
    },
  },
})

// Service workers are unreliable in the native WebView; keep them for web only
const registerServiceWorker = !Capacitor.isNativePlatform()
const isNativeApp = Capacitor.isNativePlatform()

if (isNativeApp) {
  document.documentElement.classList.add('native-app')
  void StatusBar.setOverlaysWebView({ overlay: false }).catch(() => undefined)
  void StatusBar.setStyle({ style: Style.Light }).catch(() => undefined)
  if (Capacitor.getPlatform() === 'android') {
    void StatusBar.setBackgroundColor({ color: '#ffffff' }).catch(() => undefined)
  }
}

// Register service worker
if (registerServiceWorker && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js')
      .then((registration) => {
        console.log('Service Worker registered:', registration);
        
        // Listen for service worker updates
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                // New service worker available, reload page
                console.log('New service worker available, reloading...');
                window.location.reload();
              }
            });
          }
        });
      })
      .catch((error) => {
        console.log('Service Worker registration failed:', error);
      });
  });

  // Listen for messages from service worker
  navigator.serviceWorker.addEventListener('message', (event) => {
    if (event.data && event.data.type === 'CACHE_INVALIDATED') {
      if (event.data.action === 'reload') {
        // Optionally reload the page, but we'll let React Query handle the refresh
        console.log('Cache invalidated, refreshing data...');
        // The React Query invalidation will handle the refresh
      }
    }
  });
}

const Root = () => {
  useEffect(() => {
    if (!isNativeApp) return
    NativeSplashScreen.hide({ fadeOutDuration: 0 }).catch(() => undefined)
  }, [])

  return (
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <ThemeProvider>
            <AuthProvider>
              <App />
            </AuthProvider>
          </ThemeProvider>
        </BrowserRouter>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </React.StrictMode>
  );
};

ReactDOM.createRoot(document.getElementById('root')!).render(<Root />)
